import React, {CSSProperties, useEffect, useState} from 'react';
import {Sparklines, SparklinesBars, SparklinesLine} from 'react-sparklines';
import {DatasetDataApi} from "../../../api/DatasetDataApi";
import {isNumeric} from "../../../utils/utils";
import {Tooltip} from "primereact/tooltip";
import {formatDuration, formatTime, getDiffSeconds} from "utils/time-utils";
import {Dialog} from "primereact/dialog";
import DatasetLineChart from "../charts/DatasetLineChart";

type SparkLineProps = {
    valueScaling?: number,
    decimalPlaces?: number,
    labelFontSize?: string,
    sparkLineWidth: number,
    dataset: string | number,
    count?: number,
    unit?: string,
    showMinMax?: boolean
}

const dataApi = new DatasetDataApi()

export default function SparkLine(props: SparkLineProps) {
    const [data, setData] = useState<number[]>([]);
    const [lastValueTime, setLastValueTime] = useState<Date>();
    const [loading, setLoading] = useState<boolean>(true);

    const [dialog, setDialog] = useState<boolean>(false);

    const valueScaling = props.valueScaling || 1;
    const decimalPlaces = props.decimalPlaces || 0;
    const labelFontSize = props.labelFontSize || "2rem";
    const sparklineWidth = props.sparkLineWidth || "300px";
    const count = props.count || 14;
    const dataset = isNumeric(props.dataset) ? `${props.dataset}:CATEGORIZED_DATASET` : props.dataset as string;
    const showMinMax = props.showMinMax || false;

    useEffect(() => {
        initData()
    }, []);

    const initData = () => {
        dataApi.getRecentData([dataset], count).then((r) => {
            if (r && r.data && r.data.length != 0) {
                let ob = r.data[0];
                let dataArr = ob.data.map(val => val.value);

                if (valueScaling !== 1) {
                    dataArr = dataArr.map(val => val * valueScaling)
                }

                setData(dataArr);

                if (ob.data && ob.data.length != 0) {
                    setLastValueTime(new Date(ob.data[ob.data.length - 1].key as string));
                }
            }

            setLoading(false);
        });
    }

    const style = {fontSize: labelFontSize, fontWeight: 800, whiteSpace: "nowrap", lineHeight: "2rem"} as CSSProperties;
    const id = "sparkline_value_" + (Math.random() + 1).toString(16).substring(8);

    let el;
    if (loading) {
        el = <i className="pi pi-spin pi-spinner" style={{'fontSize': '4em'}}/>
    } else {
        if (data.length !== 0) {
            const valuePre = data[data.length - 1];
            const value = decimalPlaces === 0 ? Math.round(valuePre) : valuePre.toFixed(decimalPlaces);
            const minValue = Math.min(...data);
            const maxValue = Math.max(...data);

            el = (<>
                <div style={{lineHeight: "2rem", width: sparklineWidth, height: "40px", display: "inline-block"}}>
                    <Sparklines
                        data={data}
                        height={30}
                        preserveAspectRatio={"none"}
                    >
                        <SparklinesLine style={{stroke: "#41c3f9", fill: "none"}}/>
                        <SparklinesBars style={{fill: "#41c3f9", fillOpacity: ".25"}}/>
                    </Sparklines>
                </div>
                <div id={`${id}`} className="ea-sparkline-value" style={style}>
                    {value} {props.unit}
                </div>

                <Tooltip target={`#${id}`} mouseTrack mouseTrackLeft={10}>
                    <div><b>Last value
                        time:</b> {formatTime(lastValueTime)} (age: {formatDuration(getDiffSeconds(new Date(), lastValueTime!))})
                    </div>
                    {showMinMax &&
                        <>
                            <div><b>Min:</b> {minValue} {props.unit}</div>
                            <div><b>Max:</b> {maxValue} {props.unit}</div>
                        </>
                    }

                </Tooltip>
            </>);
        } else {
            el = <h4>No data</h4>
        }
    }

    return <>
        <div style={{
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            justifyContent: "center",
            paddingBottom: "1rem",
            columnGap: "2rem"
        }}
             onDoubleClick={() => setDialog(true)}
        >
            {el}
        </div>

        <Dialog header="SCADA tags chart"
                visible={dialog}
                style={{ width: 'min(100vw, 1000px)', height:"500px"}}
                modal={false}
                onHide={() => {
                    setDialog(false);
                }}
        >

            <DatasetLineChart identifiers={[dataset]}
                              showDateSelection={true}
                              dynamicHeight={true}
            />

        </Dialog>
    </>

}